import { Sheet } from "@/domain/Sheet";
import type JSZip from "jszip";
import { OrganizationRaw } from "../../domain/Organization";
import { Version } from "../../domain/Version";
import { View } from "../../domain/View";
import { loadJSZip } from "../../utils/extLoader";
import { API_CLIENT } from "../config";
import { downloadBlobAsFile, downloadFileFromAPI } from "../helpers";
import { getAPIConfigs } from "../helpers";

const apiUrl = "/files/versions";

// ファイルパスから拡張子を推定
const getFileExtensionFromPath = (path?: string) => {
  if (!path || typeof path !== "string") {
    return undefined;
  }
  const baseName = path.split(/[/\\]/).pop() || "";
  const elements = baseName.toLowerCase().split(".");
  return elements.length > 1 ? elements[elements.length - 1] : undefined;
};

// Excelファイルアップロード時に選択するクエリ
export type SaveType = "overwrite" | "append";

export type PostSchemaResponse = { org_id: string; version: string };
export type PostDataResponse = {
  org_id: string;
  version: string;
  message: string;
};

export const getFileVersionSchema = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const versionId = version.id;
  const fileName = `${version.title}_sheet_schemas`;
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: versionId },
  };
  await downloadFileFromAPI(`${apiUrl}/schemas`, fileName, "yaml", {
    ...apiParams,
  });
};

export const getFileVersionDataJSON = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const versionId = version.id;
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: versionId },
  };
  const fileName = `${version.title}_data`;
  await downloadFileFromAPI(`${apiUrl}/data`, fileName, "json", {
    ...apiParams,
  });
};

export const getFileVersionDataExcel = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const versionId = version.id;
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: versionId },
  };
  const fileName = `${version.title}_data`;
  await downloadFileFromAPI(`${apiUrl}/data/excel`, fileName, "xlsx", {
    ...apiParams,
  });
};

export const getFileVersionDataCSV = async ({
  organizationId,
  version,
  sheet,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
  sheet: Sheet;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const versionId = version.id;
  const sheetId = sheet.id;
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: versionId,
      sheet_id: sheetId,
    },
  };
  const fileName = `${version.title}_${sheet.displayName}_data`;
  await downloadFileFromAPI(`${apiUrl}/data/csv`, fileName, "csv", {
    ...apiParams,
  });
};

export const getFileVersionResults = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const versionId = version.id;
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: versionId },
  };
  const fileName = `${version.title}_result`;
  await downloadFileFromAPI(`${apiUrl}/results`, fileName, "zip", {
    ...apiParams,
  });
};

export const getFileErrorDetail = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: version.id },
  };

  // ダウンロードしたzipファイルを取得
  const JSZip = await loadJSZip();
  const zip = new JSZip();
  try {
    const res = await API_CLIENT.get<Blob>(`${apiUrl}/results`, {
      params: apiParams.params,
      responseType: "blob",
      headers: { accept: "*/*" },
    });
    await zip.loadAsync(res.data);
  } catch (error) {
    throw new Error("エラーのファイルが見つかりませんでした。");
  }

  const findFileFromZip = async (
    zip: JSZip,
    targetFileName: string
  ): Promise<JSZip.JSZipObject | null> => {
    const files = zip.files;
    for (const fileName in files) {
      const file = files[fileName];
      if (!file.dir && file.name.endsWith(targetFileName)) {
        return file;
      }
      if (file.dir) {
        const folder = zip.folder(file.name);
        if (folder) {
          const nestedFile = await findFileFromZip(folder, targetFileName);
          if (nestedFile) {
            return nestedFile;
          }
        }
      }
    }
    return null;
  };

  const reportJsonFile = await findFileFromZip(zip, "report.json");

  if (reportJsonFile == null) {
    throw new Error("エラーのレポートが見つかりませんでした。");
  }
  // report.jsonが存在する場合、ブラウザ上でダウンロード
  const reportJsonBlob = await reportJsonFile.async("blob");

  // Blobを文字列としてパースする関数
  function blobToString(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        try {
          if (event.target) {
            resolve(event.target.result as string);
          } else {
            reject(new Error("エラーの読み込みに失敗しました。"));
          }
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = function () {
        reject(new Error("ファイルの読み込みに失敗しました。"));
      };
      reader.readAsText(blob);
    });
  }

  return blobToString(reportJsonBlob)
    .then((jsonString: string) => {
      // 受け取った文字列をjsonに変換し、keyは"input_validation_alerts"のvalueを取得
      const key = "input_validation_alerts";
      const transformedData: string[] = [];
      try {
        const json = JSON.parse(jsonString);

        const targetProperty = json[key];
        if (targetProperty === undefined) {
          throw new Error(
            `入力検証エラー(input_validation_alerts)の情報がありませんでした。`
          );
        }
        // input_validation_alertsをjsonとして見やすく整形
        if (!Array.isArray(targetProperty)) {
          throw new Error(
            `入力検証エラー(input_validation_alerts)の情報が配列になっていません。`
          );
        }
        targetProperty.forEach((item) => {
          const severity = item.severity;
          const category = item.category;
          const message = item.message;
          if (severity && category && message) {
            transformedData.push(`${severity}|${category}|${message}`);
          } else {
            console.warn(
              `item is missing one or more required properties: ${severity}, ${category}, ${message}`
            );
          }
        });
      } catch (e) {
        console.warn(e);
        throw new Error(`エラー内容のフォーマットが不正です。`);
      }
      const transformedText = transformedData.join("\r\n");
      const newBlob = new Blob([transformedText], { type: "text/plain" });
      downloadBlobAsFile(newBlob, `${version.title}_error`, "txt");
    })
    .catch((e) => {
      console.error(e);
      throw new Error("入力検証エラーの情報がありませんでした。");
    });
};
export const getFileVersionMultiResults = async ({
  organizationId,
  versionIds,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionIds: Version["id"][];
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      versions: versionIds.join(","),
    },
  };
  const fileName = `multi_results`;
  await downloadFileFromAPI(`${apiUrl}/multi_results`, fileName, "zip", {
    ...apiParams,
  });
};
export const getFileVersionInputs = async ({
  extension,
  organizationId,
  version,
}: {
  extension?: string;
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: { ...apiOrganizationIdParams?.params, version: version.id },
  };
  const fileName = `${version.title}_input`;
  const ext =
    getFileExtensionFromPath(version.optimizeJobInputFilePath ?? undefined) ??
    "zip";
  await downloadFileFromAPI(`${apiUrl}/inputs`, fileName, ext, {
    ...apiParams,
  });
};
export const getFileVersionMultiInputs = async ({
  organizationId,
  versionIds,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionIds: Version["id"][];
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      versions: versionIds.join(","),
    },
  };
  const fileName = `multi_inputs`;
  await downloadFileFromAPI(`${apiUrl}/multi_inputs`, fileName, "zip", {
    ...apiParams,
  });
};

export const getFileVersionViews = async ({
  organizationId,
  version,
}: {
  organizationId: OrganizationRaw["org_id"];
  version: Version;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: version.id,
    },
  };
  const fileName = `${version.title}_view_schemas`;
  await downloadFileFromAPI(`${apiUrl}/views`, fileName, "yaml", {
    ...apiParams,
  });
};

// スキーマ設定ファイルをアップロードしてバージョンを作成
// categoryIDを投げることで、作成されたバージョンのカテゴリを指定可能
// (初期値は空文字列=「未分類」）
export const postFileVersionSchema = async ({
  organizationId,
  title,
  comment,
  file,
  category,
}: {
  organizationId: OrganizationRaw["org_id"];
  title: string;
  comment: string;
  file: File;
  category: string;
}): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const form = new FormData();
  form.append("file", file);
  form.append("title", title);
  form.append("comment", comment);
  const response = await API_CLIENT.post<PostDataResponse>(
    `${apiUrl}/schemas`,
    form,
    {
      params: {
        ...apiOrganizationIdParams?.params,
        category,
      },
    }
  );
  return response.data;
};

// スキーマ設定ファイルをアップロードして既存バージョンを更新
export type OpType = "normal" | "dangerous";

export const postFileVersionSchemaUpdate = async ({
  organizationId,
  versionId,
  file,
  opType,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  opType: OpType;
}): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<PostDataResponse>(
    `${apiUrl}/${versionId}/schemas`,
    form,
    {
      params: {
        ...apiOrganizationIdParams?.params,
        op_type: opType,
      },
    }
  );
  return response.data;
};

export const postFileVersionData = async ({
  organizationId,
  versionId,
  file,
  saveType,
  endpoint,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  saveType: SaveType;
  endpoint: string;
}): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: versionId,
      save_type: saveType,
    },
  };
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<PostDataResponse>(endpoint, form, {
    ...apiParams,
  });
  return response.data;
};

export const postFileVersionDataExcel = async ({
  organizationId,
  versionId,
  file,
  saveType,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  saveType: SaveType;
}): Promise<PostDataResponse> => {
  return postFileVersionData({
    organizationId,
    versionId,
    file,
    saveType,
    endpoint: `${apiUrl}/data`,
  });
};

export const postFileVersionDataJSON = async ({
  organizationId,
  versionId,
  file,
  saveType,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  saveType: SaveType;
}): Promise<PostDataResponse> => {
  return postFileVersionData({
    organizationId,
    versionId,
    file,
    saveType,
    endpoint: `${apiUrl}/data/json`,
  });
};

export const postFileVersionSheetData = async ({
  organizationId,
  versionId,
  file,
  sheetId,
  saveType,
  endpoint,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  sheetId: Sheet["id"];
  saveType: SaveType;
  endpoint: string;
}): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: versionId,
      sheet_id: sheetId,
      save_type: saveType,
    },
  };
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<PostDataResponse>(endpoint, form, {
    ...apiParams,
  });
  return response.data;
};

export const postFileVersionDataCSV = async ({
  organizationId,
  versionId,
  file,
  sheetId,
  saveType,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  sheetId: Sheet["id"];
  saveType: SaveType;
}): Promise<PostDataResponse> => {
  return postFileVersionSheetData({
    organizationId,
    versionId,
    file,
    sheetId,
    saveType,
    endpoint: `${apiUrl}/data/csv`,
  });
};

// not implemented
export const postFileVersionView = async ({
  organizationId,
  versionId,
  file,
  viewId,
  viewType,
  description,
  title,
  sort,
  is_override,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  viewId: View["id"];
  file: File;
  is_override: boolean;
} & Pick<
  View,
  "description" | "viewType" | "title" | "sort"
>): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: versionId,
      view_id: viewId,
    },
  };
  const form = new FormData();
  form.append("file", file);
  form.append("description", description);
  form.append("viewType", viewType);
  form.append("title", title);
  form.append("sort", String(sort));
  form.append("is_override", String(is_override));
  const response = await API_CLIENT.post<PostDataResponse>(
    `${apiUrl}/views`,
    form,
    {
      ...apiParams,
    }
  );
  return response.data;
};

export type ViewSettingUpdateSaveType = "merge" | "replace";
export const postFileVersionViews = async ({
  organizationId,
  versionId,
  file,
  viewSettingUpdateSaveType,
}: {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  file: File;
  viewSettingUpdateSaveType: ViewSettingUpdateSaveType;
}): Promise<PostDataResponse> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    params: {
      ...apiOrganizationIdParams?.params,
      version: versionId,
      save_type: viewSettingUpdateSaveType,
    },
  };
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<PostDataResponse>(
    `${apiUrl}/views`,
    form,
    {
      ...apiParams,
    }
  );
  return response.data;
};

// Connect Job Historyのファイルダウンロード
export const downloadConnectJobHistoryFile = ({
  organizationId,
  connectId,
  connectJobHistoryId,
  connectJobFileType,
  filePath,
}: {
  organizationId: string;
  connectId: string;
  connectJobHistoryId: string;
  connectJobFileType: "input" | "output" | "artifact" | "all";
  filePath?: string;
}) => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const url = `/files/connects/${connectId}/job_histories/${connectJobHistoryId}`;
  const fileType = getFileExtensionFromPath(filePath) ?? "json";

  return downloadFileFromAPI(
    url,
    `connect_${connectId}_job_${connectJobHistoryId}_${connectJobFileType}`,
    fileType,
    {
      ...apiOrganizationIdParams,
      params: {
        ...apiOrganizationIdParams?.params,
        connect_job_file_type: connectJobFileType,
      },
    }
  );
};
